import React from 'react';
import StringGroupDetailPage from 'src/features/string-group-detail/StringGroupDetailPage';
import { Routes, Route, Navigate } from 'react-router';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import LandingEntry from 'src/features/landing/LandingEntry';
import { NamespaceDetailPage } from 'src/features/namespace';
import SearchPage from 'src/features/search/SearchPage';
import { TrackTranslationsPage } from 'src/features/track';
import StringDetailPage from 'src/features/string/StringDetailPage';
import useFederatedAuth, {
  AuthenticationState,
} from 'src/components/auth/FederatedAuth';
import { AuthContext } from 'src/components/auth/AuthContext';
import AppBootstrap from 'src/components/auth/AppBootstrap';
import AppShell from 'src/common/ui/AppShell';
import { usePageViewMetrics } from 'src/common/hooks/usePageViewMetrics';
import { FailureStatus } from 'src/common/ui/StatusScreen';
import LandingPage from 'src/features/landing/LandingPage';
import {
  CATCH_ALL_ROUTE,
  HOME_PATH,
  NAMESPACE_ROUTE,
  ROOT_PATH,
  SEARCH_PATH,
  STRING_GROUP_ROUTE,
  STRING_ROUTE,
  TRACK_MY_TRANSLATIONS_PATH,
} from 'src/common/routes';
import { useTranslation } from 'src/i18n/shim/useTranslation';

interface AppCoreProps {
  authState: AuthenticationState;
  userId: string | null;
}

// Drives page-view records on every route change via CloudWatch RUM. Renders nothing; must sit
// inside the router and is inert until RUM is initialized.
const PageViewMetrics: React.FC = () => {
  usePageViewMetrics();
  return null;
};

// The authenticated application. Routing lives here so the landing page (and
// future pages) only mount once the user is signed in and a JWT has been stored
// for the AppSync client to attach to its requests.
//
// AppBootstrap wraps the whole route table rather than sitting inside it as a
// layout route: every route is then behind the gate by construction, and no
// page-level query can be issued before the bootstrap profile query resolves.
// On failure the gate renders its error page here, in place, leaving the URL the
// user is on untouched.
const AuthenticatedApp: React.FC = () => (
  <AppBootstrap>
    <PageViewMetrics />
    <Routes>
      <Route element={<AppShell />}>
        {/* Dynamic routes are prefixed (/ns/…, /sg/…, /str/…) so no id can
            occupy a path a static route also matches: a namespace named
            "search" resolves, and adding a static route later cannot break an
            existing namespace. See src/common/routes.ts. */}
        <Route path={SEARCH_PATH} element={<SearchPage />} />
        <Route
          path={TRACK_MY_TRANSLATIONS_PATH}
          element={<TrackTranslationsPage />}
        />
        <Route path={NAMESPACE_ROUTE} element={<NamespaceDetailPage />} />
        <Route path={STRING_GROUP_ROUTE} element={<StringGroupDetailPage />} />
        {/* Optional ?variant= selects a CX-specific variant of the string. */}
        <Route path={STRING_ROUTE} element={<StringDetailPage />} />
      </Route>
      <Route path={ROOT_PATH} element={<LandingEntry />} />
      <Route path={HOME_PATH} element={<LandingPage />} />
      <Route
        path={CATCH_ALL_ROUTE}
        element={<Navigate to={ROOT_PATH} replace />}
      />
    </Routes>
  </AppBootstrap>
);

const AppCore: React.FC<AppCoreProps> = ({ authState, userId }) => {
  const { t } = useTranslation();
  const body = (() => {
    switch (authState) {
      case AuthenticationState.LOGGING_IN:
        return (
          <div id="langhub-status" role="status">
            {t('auth-logging-in')}
          </div>
        );
      case AuthenticationState.LOGGED_IN:
        // The bootstrap gate wraps routing, so the profile query resolves
        // before any route — and so any page-level query — mounts.
        return <AuthenticatedApp />;
      default:
        return (
          <FailureStatus heading={t('auth-signin-failed-heading')}>
            <SpaceBetween size="s">
              <span>{t('auth-signin-failed-body')}</span>
              {/*
                Re-running the auth flow is exactly what a reload does: on mount
                useFederatedAuth redirects an unauthenticated session to the
                Cognito hosted UI (which federates to Midway). So this button
                makes the recovery a single click instead of a manual refresh.
              */}
              <Button variant="normal" onClick={() => window.location.reload()}>
                {t('auth-midway-button')}
              </Button>
            </SpaceBetween>
          </FailureStatus>
        );
    }
  })();

  return (
    <AuthContext.Provider value={{ authState, userId }}>
      {body}
    </AuthContext.Provider>
  );
};

/** With Midway authentication (production). */
const AppWithAuth: React.FC = () => {
  const { authState, userId } = useFederatedAuth();
  return <AppCore authState={authState} userId={userId} />;
};

/** Without authentication (local/dev/test builds). */
const AppNoAuth: React.FC = () => (
  <AppCore authState={AuthenticationState.LOGGED_IN} userId="no-auth-user" />
);

const App: React.FC = NO_AUTH ? AppNoAuth : AppWithAuth;

export default App;
